import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';

import Image from './components/Image/Image'
import Search from './components/Search/Search';
import GuessList from './components/GuessList/GuessList';
import History from './components/History/History';
import Footer from './components/Footer/Footer';
import Header from './components/Header/Header';
import SkinName from './components/SkinName/SkinName';
// Landing removed; default to daily on '/'

import SkinOptionGenerator from './utils/SkinOptionGenerator'

import searchOptions from './searchOptions.json';
import { MAX_TRIES, MAX_SKIPS, SUCCESS_GUESS_COUNT, DAILY_ROUNDS } from './constants';

import './App.css';

const optionGenerator = new SkinOptionGenerator()

const EDIT_MODE = false

function App() {
  // Daily reset at 12am PST
  const todayStr = useMemo(() => {
    const now = new Date();
    // Convert to PST (UTC-8 standard, UTC-7 DST). Approximate using America/Los_Angeles.
    try {
      const pst = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
      const y = pst.getFullYear();
      const m = String(pst.getMonth() + 1).padStart(2, '0');
      const d = String(pst.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    } catch (_) {
      return new Date().toISOString().slice(0, 10);
    }
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const [isDailyMode, setIsDailyMode] = useState(() => {
    const saved = localStorage.getItem('mode');
    return saved ? saved === 'daily' : true;
  });
  const [searchOptionsCopy, setSearchOptionsCopy] = useState(JSON.parse(JSON.stringify(searchOptions)));

  const [isChallengeMode, setIsChallengeMode] = useState(false);
  const [target, setTarget] = useState({});
  const [guesses, setGuesses] = useState([]);
  const [success, setSuccess] = useState(false);
  const [streak, setStreak] = useState(0); // Interpreted as daily streak in Daily Mode
  const [highestStreak, setHighestStreak] = useState(0)
  const [historyDaily, setHistoryDaily] = useState(() => {
    try { return JSON.parse(localStorage.getItem('history:daily') || '[]'); } catch (_) { return []; }
  });
  const [historyInfinite, setHistoryInfinite] = useState([]);
  const [modifiedSkins, setModifiedSkins] = useState([]);
  const [pendingModifiedSkin, setPendingModifiedSkin] = useState(null);
  const [skipsLeft, setSkipsLeft] = useState(MAX_SKIPS);
  const [triesLeft, setTriesLeft] = useState(MAX_TRIES);
  const [imageReady, setImageReady] = useState(false);
  const [dailyRound, setDailyRound] = useState(0); // 0..DAILY_ROUNDS-1
  const [dailyCompleted, setDailyCompleted] = useState(false);
  const [dailyCountdown, setDailyCountdown] = useState('');

  useEffect(() => {
    // Hard reset transient state on mode/date change to avoid leakage
    setGuesses([]);
    setSuccess(false);
    setImageReady(false);
    setSkipsLeft(MAX_SKIPS);
    setTriesLeft(MAX_TRIES);
    setSearchOptionsCopy(JSON.parse(JSON.stringify(searchOptions)));
    // Wipe infinite history when switching modes to keep it session-only
    if (isDailyMode) {
      setHistoryInfinite([]);
    }

    if (isDailyMode) {
      // Load daily round and completion
      const savedMeta = JSON.parse(localStorage.getItem(`daily:meta:${todayStr}`) || '{}');
      const roundIndex = Number.isInteger(savedMeta.round) ? savedMeta.round : 0;
      const completed = !!savedMeta.completed;
      setDailyRound(roundIndex);
      setDailyCompleted(completed);

      const dailyTarget = optionGenerator.getDailySkinForDateAndRound(todayStr, roundIndex);
      setTarget(dailyTarget);
      setImageReady(false);

      // Load persisted daily progress
      try {
        const saved = JSON.parse(localStorage.getItem(`daily:${todayStr}:r${roundIndex}`) || '{}');
        if (saved && saved.targetId === optionGenerator.getSkinUniqueId(dailyTarget)) {
          setGuesses(saved.guesses || []);
          // If the daily is completed across all rounds, keep success true to lock input
          setSuccess(completed ? true : !!saved.success);
          setTriesLeft(typeof saved.triesLeft === 'number' ? saved.triesLeft : MAX_TRIES);
          setSkipsLeft(typeof saved.skipsLeft === 'number' ? saved.skipsLeft : MAX_SKIPS);
        } else {
          setGuesses([]);
          setSuccess(completed ? true : false);
          setTriesLeft(MAX_TRIES);
          setSkipsLeft(MAX_SKIPS);
        }

        const savedStreak = parseInt(localStorage.getItem('daily:streak') || '0', 10) || 0;
        const savedBest = parseInt(localStorage.getItem('daily:highestStreak') || '0', 10) || 0;
        setStreak(savedStreak);
        setHighestStreak(savedBest);
      } catch (_) {
        // Fallback to defaults on parse error
        setGuesses([]);
        setSuccess(completed ? true : false);
        setTriesLeft(MAX_TRIES);
        setSkipsLeft(MAX_SKIPS);
      }
    } else {
      setDailyCompleted(false);
      const randomSkinInfo = optionGenerator.getNextSkin()
      setTarget(randomSkinInfo);
      setImageReady(false);
      setSuccess(false);
    }
  }, [isDailyMode, todayStr]);

  // Listen for history selection events to set the current target
  useEffect(() => {
    const handler = (e) => {
      const item = e?.detail;
      if (!item) return;
      try {
        // Prefer canonical skin data if available
        const canonical = optionGenerator.getSkinByLoc(item.loc);
        if (canonical) {
          setTarget(canonical);
        } else {
          setTarget({
            name: item.skinName,
            champion: item.champion,
            loc: item.loc,
            maskPosition: target?.maskPosition,
          });
        }
        // Restore entire state if provided
        if (item.stateSnapshot) {
          const ss = item.stateSnapshot;
          try { setGuesses(Array.isArray(ss.guesses) ? ss.guesses : []); } catch (_) {}
          try { setSuccess(!!ss.success); } catch (_) {}
          try { setSkipsLeft(typeof ss.skipsLeft === 'number' ? ss.skipsLeft : MAX_SKIPS); } catch (_) {}
          try { setTriesLeft(typeof ss.triesLeft === 'number' ? ss.triesLeft : MAX_TRIES); } catch (_) {}
          try { setIsChallengeMode(!!ss.isChallengeMode); } catch (_) {}
        } else if (Array.isArray(item.guessesSnapshot)) {
          setGuesses(item.guessesSnapshot);
          setSuccess(true);
        } else {
          setSuccess(true);
        }
        setImageReady(false);
      } catch (_) {}
    };
    window.addEventListener('history:select', handler);
    return () => window.removeEventListener('history:select', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep isDailyMode in sync with route ('/' => daily, '/infinite' => infinite)
  useEffect(() => {
    if (location.pathname === '/infinite') {
      setIsDailyMode(false);
    } else {
      setIsDailyMode(true);
    }
  }, [location.pathname]);

  // Daily countdown to next reset at 12:00:00 AM America/Los_Angeles
  useEffect(() => {
    if (!isDailyMode) {
      setDailyCountdown('');
      return;
    }
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
    const tick = () => {
      const now = new Date();
      const parts = fmt.formatToParts(now).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
      const h = parseInt(parts.hour || '0', 10) || 0;
      const m = parseInt(parts.minute || '0', 10) || 0;
      const s = parseInt(parts.second || '0', 10) || 0;
      let remaining = 24 * 3600 - (h * 3600 + m * 60 + s);
      if (remaining <= 0) remaining = 24 * 3600; // wrap at midnight
      const rh = String(Math.floor(remaining / 3600)).padStart(2, '0');
      const rm = String(Math.floor((remaining % 3600) / 60)).padStart(2, '0');
      const rs = String(remaining % 60).padStart(2, '0');
      setDailyCountdown(`${rh}:${rm}:${rs}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isDailyMode]);

  // Removed aggressive cleanup to avoid state bugs; consider a manual cleanup UI if needed

  // Persist daily progress as you play
  useEffect(() => {
    if (!isDailyMode) return;
    if (!target || !target.champion) return;
    try {
      const payload = {
        targetId: optionGenerator.getSkinUniqueId(target),
        guesses,
        success,
        triesLeft,
        skipsLeft,
      };
      localStorage.setItem(`daily:${todayStr}:r${dailyRound}`, JSON.stringify(payload));
    } catch (_) {}
  }, [isDailyMode, target, guesses, success, triesLeft, skipsLeft, todayStr, dailyRound]);

  const handleMaskDragEnd = ({ xPercent, yPercent }) => {
    const maskPosition = `${xPercent.toFixed(2)}% ${yPercent.toFixed(2)}%`;
    const updatedSkin = { ...target, maskPosition };
    setPendingModifiedSkin(updatedSkin);
  };

  const guessHandler = (val) => {
    const nextGuesses = [val, ...guesses];
    setGuesses(nextGuesses);
    
    if (val === target.champion) {
      setSuccess(true);
      if (isDailyMode) {
        // Track per-day round results
        const resultsKey = `daily:${todayStr}:results`;
        let results = [];
        try { results = JSON.parse(localStorage.getItem(resultsKey) || '[]'); } catch (_) { results = []; }
        results[dailyRound] = { win: true, guesses: (guesses.length + 1) };
        try { localStorage.setItem(resultsKey, JSON.stringify(results)); } catch (_) {}

        // Update daily streaks based on consecutive day wins only when finishing all rounds
        const lastWin = localStorage.getItem('daily:lastWinDate');
        const yesterday = new Date(new Date(todayStr).getTime() - 24 * 60 * 60 * 1000).toISOString().slice(0,10);
        const nextStreak = lastWin === yesterday ? (streak + 1) : 1;
        const nextBest = Math.max(highestStreak, nextStreak);
        // Only update and persist at the end of all rounds
        if (dailyRound + 1 >= DAILY_ROUNDS) {
          setStreak(nextStreak);
          setHighestStreak(nextBest);
          localStorage.setItem('daily:lastWinDate', todayStr);
          localStorage.setItem('daily:streak', String(nextStreak));
          localStorage.setItem('daily:highestStreak', String(nextBest));
          setDailyCompleted(true);
          localStorage.setItem(`daily:meta:${todayStr}`, JSON.stringify({ round: dailyRound, completed: true }));
        }
      } else {
        setStreak(streak + 1);
        setHighestStreak(Math.max(highestStreak, streak + 1))
      }
      
      // Add to history when successful (store full state snapshot for daily replay)
      const historyItem = {
        skinName: target.name,
        champion: target.champion,
        guessCount: guesses.length + 1, // +1 because we just added the correct guess
        challengeMode: isChallengeMode,
        isDaily: isDailyMode,
        guessesSnapshot: nextGuesses,
        stateSnapshot: {
          target,
          guesses: nextGuesses,
          success: true,
          triesLeft,
          skipsLeft,
          isChallengeMode,
          dailyRound,
          isDailyMode,
        },
        loc: target.loc // used for hover preview image in History
      };
      if (isDailyMode) {
        const next = [historyItem, ...historyDaily];
        setHistoryDaily(next);
        try { localStorage.setItem('history:daily', JSON.stringify(next)); } catch (_) {}
      } else {
        const next = [historyItem, ...historyInfinite];
        setHistoryInfinite(next);
      }
    } else {
      setSearchOptionsCopy(searchOptionsCopy.toSpliced(searchOptionsCopy.findIndex(i => i.value === val), 1));
      if (!isDailyMode) {
        setStreak(0);
      }
      
      // Decrease tries left
      const newTriesLeft = triesLeft - 1;
      setTriesLeft(newTriesLeft);
      
      // If out of tries, trigger success without incrementing streak
      if (newTriesLeft <= 0) {
        setSuccess(true);
        if (isDailyMode) {
          // Losing the daily resets the daily streak
          setStreak(0);
          localStorage.setItem('daily:streak', '0');
          const best = parseInt(localStorage.getItem('daily:highestStreak') || '0', 10) || 0;
          setHighestStreak(best);
          if (dailyRound + 1 >= DAILY_ROUNDS) {
            setDailyCompleted(true);
            localStorage.setItem(`daily:meta:${todayStr}`, JSON.stringify({ round: dailyRound, completed: true }));
          }
        }
        // Add to history when out of tries
        const historyItem = {
          skinName: target.name,
          champion: target.champion,
          guessCount: guesses.length + 1,
          challengeMode: isChallengeMode,
          isDaily: isDailyMode,
          outOfTries: true,
          guessesSnapshot: nextGuesses,
          stateSnapshot: {
            target,
            guesses: nextGuesses,
            success: true,
            triesLeft: 0,
            skipsLeft,
            isChallengeMode,
            dailyRound,
            isDailyMode,
          },
          loc: target.loc
        };
        if (isDailyMode) {
          const next = [historyItem, ...historyDaily];
          setHistoryDaily(next);
          try { localStorage.setItem('history:daily', JSON.stringify(next)); } catch (_) {}
          // Persist round result for daily
          const resultsKey = `daily:${todayStr}:results`;
          let results = [];
          try { results = JSON.parse(localStorage.getItem(resultsKey) || '[]'); } catch (_) { results = []; }
          results[dailyRound] = { win: false, guesses: (guesses.length + 1) };
          try { localStorage.setItem(resultsKey, JSON.stringify(results)); } catch (_) {}
        } else {
          const next = [historyItem, ...historyInfinite];
          setHistoryInfinite(next);
        }
      }
    }
  };

  const nextHandler = () => {
    if (EDIT_MODE) {
      // Add/update modified skin and log the updated array
      let updated = [pendingModifiedSkin, ...modifiedSkins]
      setModifiedSkins(updated)
      console.log(updated)
    }

    if (isDailyMode) {
      // Advance round or complete
      const nextRound = dailyRound + 1;
      if (nextRound < DAILY_ROUNDS) {
        setDailyRound(nextRound);
        localStorage.setItem(`daily:meta:${todayStr}`, JSON.stringify({ round: nextRound, completed: false }));
        const nextTarget = optionGenerator.getDailySkinForDateAndRound(todayStr, nextRound);
        setTarget(nextTarget);
        // Load saved progress for next round if exists
        const saved = JSON.parse(localStorage.getItem(`daily:${todayStr}:r${nextRound}`) || '{}');
        if (saved && saved.targetId === optionGenerator.getSkinUniqueId(nextTarget)) {
          setGuesses(saved.guesses || []);
          setSuccess(!!saved.success);
          // setTriesLeft(typeof saved.triesLeft === 'number' ? saved.triesLeft : DAILY_TRIES);
          setSkipsLeft(typeof saved.skipsLeft === 'number' ? saved.skipsLeft : MAX_SKIPS);
        } else {
          setGuesses([]);
          setSuccess(false);
          // setTriesLeft(DAILY_TRIES);
          setSkipsLeft(MAX_SKIPS);
        }
        setImageReady(false);
        return;
      } else {
        // Already completed all rounds
        setDailyCompleted(true);
        localStorage.setItem(`daily:meta:${todayStr}`, JSON.stringify({ round: dailyRound, completed: true }));
        return;
      }
    }

    setGuesses([])
    setSearchOptionsCopy(JSON.parse(JSON.stringify(searchOptions)));
    setTarget(optionGenerator.getNextSkin())
    setSuccess(false);
    setTriesLeft(MAX_TRIES); // Reset tries for new skin
    setImageReady(false);
  };

  const skipHandler = () => {
    if (skipsLeft <= 0) return;
    const historyItem = {
      skinName: target.name,
      champion: target.champion,
      guessCount: guesses.length,
      challengeMode: isChallengeMode,
      skipped: true,
      loc: target.loc
    };
    if (isDailyMode) {
      const next = [historyItem, ...historyDaily];
      setHistoryDaily(next);
      try { localStorage.setItem('history:daily', JSON.stringify(next)); } catch (_) {}
    } else {
      const next = [historyItem, ...historyInfinite];
      setHistoryInfinite(next);
    }
    setSkipsLeft(skipsLeft - 1);
    setSuccess(true);
    if (isDailyMode) {
      setStreak(0);
      localStorage.setItem('daily:streak', '0');
    }
  };

  const challengeModeHandler = (e) => {
    const nextValue = e?.target?.checked ?? !isChallengeMode;
    if (nextValue === isChallengeMode) return;
    const confirmed = window.confirm('Toggling Challenge Mode will reset your current streak. Continue?');
    if (!confirmed) {
      return;
    }
    setIsChallengeMode(nextValue);
    setStreak(0);
  };

  const GameLayout = (
    <div className="App">
      <Header
        mode={isDailyMode ? 'daily' : 'infinite'}
        onChangeMode={(mode) => {
          const daily = mode === 'daily';
          setIsDailyMode(daily);
          localStorage.setItem('mode', mode);
          // When switching modes live, reset game state appropriately
          setGuesses([]);
          setSearchOptionsCopy(JSON.parse(JSON.stringify(searchOptions)));
          setSuccess(false);
          setTriesLeft(MAX_TRIES);
          setSkipsLeft(MAX_SKIPS);
          // Clear infinite history on any mode switch
          setHistoryInfinite([]);
          if (daily) {
            navigate('/');
            const meta = JSON.parse(localStorage.getItem(`daily:meta:${todayStr}`) || '{}');
            const roundIndex = Number.isInteger(meta.round) ? meta.round : 0;
            const dailyTarget = optionGenerator.getDailySkinForDateAndRound(todayStr, roundIndex);
            setTarget(dailyTarget);
            const saved = JSON.parse(localStorage.getItem(`daily:${todayStr}:r${roundIndex}`) || '{}');
            if (saved && saved.targetId === optionGenerator.getSkinUniqueId(dailyTarget)) {
              setGuesses(saved.guesses || []);
              setSuccess(meta.completed ? true : !!saved.success);
              setTriesLeft(typeof saved.triesLeft === 'number' ? saved.triesLeft : MAX_TRIES);
              setSkipsLeft(typeof saved.skipsLeft === 'number' ? saved.skipsLeft : MAX_SKIPS);
            }
          } else {
            navigate('/infinite');
            setTarget(optionGenerator.getNextSkin());
            setSuccess(false);
          }
        }}
        onOpenLanding={() => navigate('/')}
      />
      <div className="main">
        <History history={isDailyMode ? historyDaily : historyInfinite} />
        <div className="game-container">
          <h2 style={{ color: '#e6c200', margin: '6px 0', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
            {isDailyMode
              ? `Daily (${new Date().toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' })})`
              : 'Infinite'}
          </h2>
          
          {!success && <Image target={target} guessCount={guesses.length} isChallengeMode={isChallengeMode} onMaskDragEnd={handleMaskDragEnd} onReady={() => setImageReady(true)} />}
          {success && <Image target={target} guessCount={SUCCESS_GUESS_COUNT} isWin={success} onReady={() => setImageReady(true)} />}
          {!(isDailyMode && dailyCompleted) && (
            <div className="streak-and-challenge-toggle">
              {!isDailyMode && (
                <div>
                  <h2 style={{ color: 'white', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>{`Streak: ${streak}`}</h2>
                  <h2 style={{ color: 'white', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>{`Session Best: ${highestStreak}`}</h2>
                </div>
              )}
              {isDailyMode && (
                <div>
                  <h2 style={{ color: 'white', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>{`Round ${dailyRound + 1}/${DAILY_ROUNDS}`}</h2>
                </div>
              )}
              {!isDailyMode && (
                <div className="challenge-mode-toggle-container">
                  <div className="checkbox-wrapper-3">
                    <input type="checkbox" id="cbx-3" checked={isChallengeMode} onChange={challengeModeHandler} />
                    <label htmlFor="cbx-3" className="toggle"><span></span></label>
                  </div>
                  <h2 style={{ color: 'white', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Challenge Mode</h2>
                </div>
              )}
              {isDailyMode && !dailyCompleted && (
                <div className="challenge-mode-toggle-container">
                  <div className="checkbox-wrapper-3">
                    <input type="checkbox" id="cbx-3" checked={isChallengeMode} onChange={challengeModeHandler} />
                    <label htmlFor="cbx-3" className="toggle"><span></span></label>
                  </div>
                  <h2 style={{ color: 'white', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Challenge Mode</h2>
                </div>
              )}
            </div>
          )}
          {success && <SkinName text={target.name} />}
          {success && !isDailyMode && <button className="next-button" onClick={nextHandler}>Next</button>}
          {success && isDailyMode && (
            <div style={{ marginTop: 0 }}>
              {(!dailyCompleted && dailyRound + 1 < DAILY_ROUNDS) ? (
                <button className="next-button" onClick={nextHandler}>Next Round</button>
              ) : (
                <h2 style={{ color: 'white', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000' }}>
                  {`Daily complete! Come back in ${dailyCountdown || '00:00:00'} for more. `}
                  <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => navigate('/infinite')}>Or try infinite mode</span>
                </h2>
              )}
            </div>
          )}
          {!success && imageReady && <Search guessHandler={guessHandler} searchOptions={searchOptionsCopy} />}
          {!success && imageReady && !isDailyMode && (
            <button className="skip-button" onClick={skipHandler} disabled={skipsLeft <= 0}>
              {skipsLeft > 0 ? `Skip (${skipsLeft} left)` : 'Skip (0 left)'}
            </button>
          )}
          <GuessList li={guesses} target={target} />
        </div>
      </div>
      <Footer />
    </div>
  );

  return (
    <Routes>
      <Route path="/" element={GameLayout} />
      <Route path="/infinite" element={GameLayout} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
